import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, Save, MapPin, Plus, Pencil, Mail } from "lucide-react";

const ACTION_COLORS = {
  login: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  login_failed: "bg-red-500/15 text-red-400 border-red-500/30",
  create: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  update: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  delete: "bg-red-500/15 text-red-400 border-red-500/30",
  close: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  reopen: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  assign: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  unassign: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  refund: "bg-pink-500/15 text-pink-400 border-pink-500/30",
  email: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  role_change: "bg-purple-500/15 text-purple-400 border-purple-500/30",
};

function RatesTab() {
  const [rates, setRates] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const dirty = rates && JSON.stringify({
    sales_tax_rate: parseFloat(form.sales_tax_rate),
    bcf_charge_rate: parseFloat(form.bcf_charge_rate),
    bcf_remit_rate: parseFloat(form.bcf_remit_rate),
    pa_fee_amount: parseFloat(form.pa_fee_amount),
    cg_fee_amount: parseFloat(form.cg_fee_amount),
    noshow_grace_minutes: parseInt(form.noshow_grace_minutes, 10),
  }) !== JSON.stringify({
    sales_tax_rate: rates.sales_tax_rate,
    bcf_charge_rate: rates.bcf_charge_rate,
    bcf_remit_rate: rates.bcf_remit_rate,
    pa_fee_amount: rates.pa_fee_amount,
    cg_fee_amount: rates.cg_fee_amount,
    noshow_grace_minutes: rates.noshow_grace_minutes ?? 5,
  });

  useEffect(() => { (async () => {
    try {
      const res = await api.get("/settings");
      setRates(res.data);
      setForm({
        sales_tax_rate: (res.data.sales_tax_rate * 100).toFixed(4),
        bcf_charge_rate: (res.data.bcf_charge_rate * 100).toFixed(4),
        bcf_remit_rate: (res.data.bcf_remit_rate * 100).toFixed(4),
        pa_fee_amount: res.data.pa_fee_amount.toFixed(2),
        cg_fee_amount: res.data.cg_fee_amount.toFixed(2),
        noshow_grace_minutes: String(res.data.noshow_grace_minutes ?? 5),
      });
    } catch (e) { toast.error("Failed to load rates"); }
  })(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const grace = Math.max(1, Math.min(60, parseInt(form.noshow_grace_minutes, 10) || 5));
      await api.put("/settings", {
        sales_tax_rate: parseFloat(form.sales_tax_rate) / 100,
        bcf_charge_rate: parseFloat(form.bcf_charge_rate) / 100,
        bcf_remit_rate: parseFloat(form.bcf_remit_rate) / 100,
        pa_fee_amount: parseFloat(form.pa_fee_amount),
        cg_fee_amount: parseFloat(form.cg_fee_amount),
        noshow_grace_minutes: grace,
      });
      toast.success("Rates saved");
      const fresh = await api.get("/settings");
      setRates(fresh.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  if (!rates) return <div className="text-muted-foreground text-sm py-8">Loading...</div>;

  const RateRow = ({ label, field, suffix, help }) => (
    <div className="grid grid-cols-[1fr_180px] items-center gap-4 py-3 border-b border-border last:border-b-0">
      <div>
        <Label className="text-sm text-foreground">{label}</Label>
        {help && <p className="text-[11px] text-muted-foreground mt-0.5">{help}</p>}
      </div>
      <div className="relative">
        <Input
          type="number" step="0.0001" min="0"
          value={form[field] ?? ""}
          onChange={(e) => setForm({ ...form, [field]: e.target.value })}
          className="bg-muted/20 border-border h-9 pr-10 text-right tabular-nums"
          data-testid={`rate-${field}`}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{suffix}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 max-w-2xl" data-testid="rates-tab">
      <div className="rounded-lg border border-border p-4">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Tax</div>
        <RateRow label="NY Sales Tax Rate" field="sales_tax_rate" suffix="%" help="Applied to fare on NY→NY trips only. Default 8.875% (NYC combined rate)." />
      </div>

      <div className="rounded-lg border border-border p-4">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">NYS Black Car Fund</div>
        <RateRow label="BCF Charge (customer pays)" field="bcf_charge_rate" suffix="%" help="Charged to customer on fare. Default 2.5%." />
        <RateRow label="BCF Remit (you pay agency)" field="bcf_remit_rate" suffix="%" help="What you owe NYS BCF. Default 2.44%." />
      </div>

      <div className="rounded-lg border border-border p-4">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Fees</div>
        <RateRow label="Port Authority Fee" field="pa_fee_amount" suffix="$" help="Flat fee per airport leg (JFK / LGA / EWR)." />
        <RateRow label="Congestion Fee" field="cg_fee_amount" suffix="$" help="Flat fee for Manhattan <96th St trips, when NY→NY." />
      </div>

      <div className="rounded-lg border border-border p-4">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Driver App</div>
        <div className="grid grid-cols-[1fr_180px] items-center gap-4 py-3">
          <div>
            <Label className="text-sm text-foreground">No-Show Grace Period</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">After a driver taps "Arrived at Pickup", how long before they can mark a No-Show. Range 1–60 minutes. Default 5.</p>
          </div>
          <div className="relative">
            <Input
              type="number" step="1" min="1" max="60"
              value={form.noshow_grace_minutes ?? ""}
              onChange={(e) => setForm({ ...form, noshow_grace_minutes: e.target.value })}
              className="bg-muted/20 border-border h-9 pr-10 text-right tabular-nums"
              data-testid="rate-noshow_grace_minutes"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">min</span>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button onClick={save} disabled={saving || !dirty} data-testid="save-rates-btn">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Rates"}
        </Button>
      </div>
    </div>
  );
}

function TeamTab({ currentUser }) {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "team_member" });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/auth/team");
      setTeam(res.data);
    } catch (e) { toast.error("Failed to load team"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!inviteForm.email) { toast.error("Email required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/register", inviteForm);
      toast.success(data.email_sent ? `Invited ${data.email} — temp password emailed` : `Invited ${data.email} — email delivery issue, use Resend welcome`);
      setInviteOpen(false);
      setInviteForm({ email: "", name: "", role: "team_member" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const resendWelcome = async (user) => {
    try {
      const { data } = await api.post(`/auth/team/${user.id}/resend-welcome`);
      if (data.email_status === "sent") toast.success(`Welcome email resent to ${user.email}`);
      else toast.message(data.email_status || "Done");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const changeRole = async (user, role) => {
    try {
      await api.put(`/auth/team/${user.id}/role`, { role });
      toast.success(`Role updated`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async () => {
    try {
      await api.delete(`/auth/team/${deleteTarget.id}`);
      toast.success("Team member removed");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="team-tab">
      <div className="flex justify-end">
        <Button onClick={() => setInviteOpen(true)} data-testid="invite-member-btn">
          <UserPlus className="w-4 h-4 mr-1.5" /> Invite Team Member
        </Button>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/20 hover:bg-muted/20">
              <TableHead>Email</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : team.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No team members.</TableCell></TableRow>
            ) : team.map((u) => {
              const isSelf = u.email === currentUser?.email;
              return (
                <TableRow key={u.id} data-testid={`team-row-${u.id}`}>
                  <TableCell className="text-sm">{u.email}{isSelf && <span className="ml-2 text-[10px] text-muted-foreground/60">(you)</span>}</TableCell>
                  <TableCell className="text-sm">{u.name || "—"}</TableCell>
                  <TableCell>
                    {isSelf ? (
                      <Badge className={u.role === "admin" ? "bg-primary/20 text-primary border-primary/40 text-[10px]" : "bg-muted/20 text-muted-foreground text-[10px]"}>
                        {u.role}
                      </Badge>
                    ) : (
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                        <SelectTrigger className="w-[140px] h-7 text-xs bg-card border-border" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="team_member">Team Member</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {!isSelf && (
                      <div className="inline-flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => resendWelcome(u)} className="h-7 px-2 text-muted-foreground hover:text-primary" data-testid={`resend-welcome-${u.id}`} title="Resend welcome email with new temp password">
                          <Mail className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(u)} className="h-7 px-2 text-red-400 hover:bg-red-500/10" data-testid={`delete-member-${u.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>A temporary password will be emailed to this address. They'll be prompted to set their own password on first login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs text-muted-foreground">Email *</Label><Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} data-testid="invite-email" /></div>
            <div><Label className="text-xs text-muted-foreground">Name</Label><Input value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} data-testid="invite-name" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Role</Label>
              <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="team_member">Team Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button onClick={invite} disabled={saving} data-testid="invite-save">{saving ? "Sending..." : "Send Invite"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleteTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>They will lose access immediately. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-500/20 text-red-400 border-red-500/40 hover:bg-red-500/30" data-testid="delete-member-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AuditTab({ initialAction = "all", initialResource = "all" }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState(initialAction);
  const [resourceFilter, setResourceFilter] = useState(initialResource);
  const limit = 30;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (actionFilter !== "all") params.set("action", actionFilter);
      if (resourceFilter !== "all") params.set("resource_type", resourceFilter);
      const { data } = await api.get(`/audit-logs?${params}`);
      setLogs(data.logs);
      setTotal(data.total);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, actionFilter, resourceFilter]);
  useEffect(() => { setPage(1); }, [actionFilter, resourceFilter]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4" data-testid="audit-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-[160px] bg-card border-border h-8 text-sm" data-testid="audit-action-filter"><SelectValue placeholder="All actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            <SelectItem value="login">Login</SelectItem>
            <SelectItem value="login_failed">Login failed</SelectItem>
            <SelectItem value="create">Create</SelectItem>
            <SelectItem value="update">Update</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
            <SelectItem value="close">Close</SelectItem>
            <SelectItem value="reopen">Reopen</SelectItem>
            <SelectItem value="assign">Assign</SelectItem>
            <SelectItem value="unassign">Unassign</SelectItem>
            <SelectItem value="refund">Refund</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="role_change">Role change</SelectItem>
          </SelectContent>
        </Select>
        <Select value={resourceFilter} onValueChange={setResourceFilter}>
          <SelectTrigger className="w-[160px] bg-card border-border h-8 text-sm" data-testid="audit-resource-filter"><SelectValue placeholder="All resources" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All resources</SelectItem>
            <SelectItem value="session">Session</SelectItem>
            <SelectItem value="job">Job</SelectItem>
            <SelectItem value="driver">Driver</SelectItem>
            <SelectItem value="invoice">Invoice</SelectItem>
            <SelectItem value="payment">Payment</SelectItem>
            <SelectItem value="location">Location</SelectItem>
            <SelectItem value="user">User</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">{total} entries</span>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/20 hover:bg-muted/20">
              <TableHead className="w-[160px]">Timestamp</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead className="w-[110px]">Action</TableHead>
              <TableHead className="w-[90px]">Resource</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No audit entries.</TableCell></TableRow>
            ) : logs.map((l) => (
              <TableRow key={l.id} data-testid={`audit-row-${l.id}`}>
                <TableCell className="text-xs tabular-nums" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{(l.timestamp || "").replace("T", " ").slice(0, 19)}</TableCell>
                <TableCell className="text-sm">{l.actor}</TableCell>
                <TableCell><Badge className={`text-[10px] ${ACTION_COLORS[l.action] || "bg-muted/20 text-muted-foreground"}`}>{l.action}</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground">{l.resource_type}</TableCell>
                <TableCell className="text-xs text-muted-foreground truncate max-w-[300px]" title={l.details}>{l.details || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {total > limit && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="audit-prev">Prev</Button>
            <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="audit-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function LocationsTab() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", aliases: "", category: "airport", auto_flags_airport: true, auto_flags_ny: true });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/locations");
      setLocations(res.data);
    } catch { toast.error("Failed to load locations"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditTarget("new");
    setForm({ name: "", address: "", aliases: "", category: "airport", auto_flags_airport: true, auto_flags_ny: true });
  };

  const openEdit = (loc) => {
    setEditTarget(loc);
    setForm({
      name: loc.name,
      address: loc.address || "",
      aliases: (loc.aliases || []).join(", "),
      category: loc.category || "airport",
      auto_flags_airport: !!loc.auto_flags?.is_airport_trip,
      auto_flags_ny: loc.auto_flags?.ny_to_ny !== false,
    });
  };

  const saveLocation = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    const auto_flags = {};
    if (form.auto_flags_airport) auto_flags.is_airport_trip = true;
    if (!form.auto_flags_ny) auto_flags.ny_to_ny = false;
    const payload = {
      name: form.name.trim(),
      address: form.address.trim(),
      aliases: form.aliases.split(",").map((a) => a.trim()).filter(Boolean),
      category: form.category,
      auto_flags,
    };
    try {
      if (editTarget === "new") {
        await api.post("/locations", payload);
        toast.success("Location added");
      } else {
        await api.put(`/locations/${editTarget.id}`, payload);
        toast.success("Location updated");
      }
      setEditTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/locations/${deleteTarget.id}`);
      toast.success("Location deleted");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="locations-tab">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{locations.length} saved locations</span>
        <Button size="sm" onClick={openCreate} data-testid="add-location-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Location
        </Button>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/20 hover:bg-muted/20">
              <TableHead>Name</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Aliases</TableHead>
              <TableHead className="w-[100px]">Flags</TableHead>
              <TableHead className="text-right w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : locations.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No saved locations yet.</TableCell></TableRow>
            ) : locations.map((loc) => (
              <TableRow key={loc.id} data-testid={`location-row-${loc.id}`}>
                <TableCell className="text-sm font-medium">{loc.name}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[250px] truncate">{loc.address || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{(loc.aliases || []).join(", ") || "—"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {loc.auto_flags?.is_airport_trip && <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/30 text-[9px]">APT</Badge>}
                    {loc.auto_flags?.ny_to_ny === false && <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[9px]">OOS</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(loc)} className="h-7 px-2" data-testid={`edit-location-${loc.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(loc)} className="h-7 px-2 text-red-400 hover:bg-red-500/10" data-testid={`delete-location-${loc.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle>{editTarget === "new" ? "Add Location" : "Edit Location"}</DialogTitle>
            <DialogDescription>Saved locations appear first in address autocomplete.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs text-muted-foreground">Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="location-name" placeholder="e.g. JFK — Terminal 4" /></div>
            <div><Label className="text-xs text-muted-foreground">Full Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="location-address" placeholder="Full address" /></div>
            <div><Label className="text-xs text-muted-foreground">Aliases (comma separated)</Label><Input value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} data-testid="location-aliases" placeholder="jfk, jfk t4, kennedy" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="location-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="airport">Airport</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-4 pt-1">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={form.auto_flags_airport} onChange={(e) => setForm({ ...form, auto_flags_airport: e.target.checked })} className="rounded" />
                Auto-set PA Fee
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={!form.auto_flags_ny} onChange={(e) => setForm({ ...form, auto_flags_ny: !e.target.checked })} className="rounded" />
                Out-of-state (Tax Exempt)
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={saveLocation} disabled={saving} data-testid="location-save">{saving ? "Saving..." : editTarget === "new" ? "Add" : "Update"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card border-border" data-testid="delete-location-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This location will be removed from the autocomplete list.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500/20 text-red-400 border-red-500/40 hover:bg-red-500/30" data-testid="delete-location-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function AdminSettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qTab = searchParams.get("tab");
  const qAction = searchParams.get("action") || "all";
  const qResource = searchParams.get("resource") || "all";
  const [tab, setTab] = useState(qTab || "rates");

  useEffect(() => {
    if (user && user.role !== "admin") navigate("/", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (qTab) setTab(qTab);
  }, [qTab]);

  if (!user || user.role !== "admin") return null;

  return (
    <div data-testid="admin-settings-page" className="space-y-6">
      <div>
        <h1 className="text-3xl tracking-tight font-light flex items-center gap-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <ShieldCheck className="w-6 h-6 text-primary" /> Admin Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Pricing rates · Team management · Locations · Audit log</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="rates" data-testid="tab-rates">Rates</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Team</TabsTrigger>
          <TabsTrigger value="locations" data-testid="tab-locations">Locations</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
        </TabsList>
        <TabsContent value="rates" className="pt-4"><RatesTab /></TabsContent>
        <TabsContent value="team" className="pt-4"><TeamTab currentUser={user} /></TabsContent>
        <TabsContent value="locations" className="pt-4"><LocationsTab /></TabsContent>
        <TabsContent value="audit" className="pt-4"><AuditTab initialAction={qAction} initialResource={qResource} /></TabsContent>
      </Tabs>
    </div>
  );
}
